/**
 * @module ui/ui_human-urls/static-filter-widget
 * @description
 * Легковесный класс фильтра для обычных (не-AJAX) виджетов с человекочитаемыми URL
 */

import * as HumanReadableUrls from '@/ui/ui_human-urls';

/**
 * Класс для работы с человекочитаемыми URL в статических виджетах фильтров
 * (виджеты с кнопкой "Применить", без AJAX)
 */
export class StaticFilterWidget {
  constructor(formNode) {
    this.formNode = formNode;
    this.json = null;
    this._permalinkMaps = null;
  }

  /**
   * Инициализация: загрузка данных коллекции и построение мап
   */
  async init() {
    try {
      await this._loadCollectionData();
      this._buildPermalinkMaps();
      return true;
    } catch (error) {
      console.error('[HumanReadableUrls] Failed to initialize filter widget:', error);
      return false;
    }
  }

  /**
   * Загрузка данных коллекции из глобального объекта, data-атрибута или API
   */
  async _loadCollectionData() {
    // Попытка получить из глобального объекта
    if (window.collectionData && typeof window.collectionData === 'object') {
      this.json = window.collectionData;
      return;
    }

    if (window.collection && typeof window.collection === 'object') {
      this.json = window.collection;
      return;
    }

    // Попытка прочитать из data-атрибута формы
    const dataCollection = this.formNode?.dataset?.collection;
    if (dataCollection) {
      try {
        const parsed = JSON.parse(dataCollection);
        // Валидация структуры JSON
        if (parsed && typeof parsed === 'object' && parsed.status === 'ok') {
          this.json = parsed;
          return;
        } else {
          console.warn('[HumanReadableUrls] Invalid JSON structure in data-collection attribute');
        }
      } catch (e) {
        console.warn('[HumanReadableUrls] Failed to parse data-collection attribute:', e);
      }
    }

    // Загрузка через API
    await this._fetchCollectionData();
  }

  /**
   * Загрузка данных через API
   */
  async _fetchCollectionData() {
    if (location.pathname.indexOf('collection') === -1) {
      throw new Error('Not a collection page');
    }

    const apiPath = '/front_api' + location.pathname + location.search;
    const response = await fetch(apiPath);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    this.json = await response.json();
  }

  /**
   * Построение мап id → permalink для быстрого поиска
   */
  _buildPermalinkMaps() {
    if (!this.json) {
      this._permalinkMaps = {
        propertyIdToPermalink: {},
        characteristicIdToPermalink: {},
        optionNameIdToPermalink: {},
        optionValueIdToPermalink: {}
      };
      return;
    }

    this._permalinkMaps = HumanReadableUrls.buildPermalinkMaps(this.json);
  }

  /**
   * Получение текущего состояния фильтров из JSON данных
   */
  getCurrentState() {
    if (!this.json) {
      return {
        selectedCharacteristicIds: [],
        selectedOptionValueIdsByOptionNameId: {},
        priceMin: null,
        priceMax: null,
        order: null
      };
    }

    return HumanReadableUrls.getCurrentState(this.json);
  }

  /**
   * Построение человекочитаемого URL на основе состояния фильтров
   */
  buildHumanReadableUrl(state) {
    if (!this.json || !this._permalinkMaps) {
      return null;
    }

    const currentState = state || this.getCurrentState();
    return HumanReadableUrls.buildHumanReadableUrl(
      currentState,
      this._permalinkMaps,
      this.json
    );
  }
}
